import React, { useState, useEffect, useCallback } from 'react';
import { 
  BookOpen, Plus, Edit2, Trash2, RefreshCw, Save, Eye, 
  Image, Sparkles, Send, ChevronDown, ChevronUp, 
  FileText, Calendar, User, Quote, Newspaper, Loader2, Wand2, Twitter, PenTool
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import * as api from '../../services/api';

const EditionsManager = () => {
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEdition, setEditingEdition] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genTaskId, setGenTaskId] = useState(null);
  const [genStatus, setGenStatus] = useState(null);
  const [tweetModal, setTweetModal] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [addingArticle, setAddingArticle] = useState(false);
  const [addingProgress, setAddingProgress] = useState(0); // {editionId, articleIndex, tweetText, loading, title}

  const fetchEditions = useCallback(async () => {
    try {
      const data = await api.getEditions();
      setEditions(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEditions();
  }, [fetchEditions]);

  const handleDelete = async (editionId) => {
    if (!window.confirm('¿Eliminar esta edición?')) return;
    setProcessing(editionId);
    try {
      await api.deleteEdition(editionId);
      await fetchEditions();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handlePublish = async (editionId) => {
    setProcessing(editionId + '_pub');
    try {
      await api.publishEdition(editionId);
      await fetchEditions();
      alert('Edición publicada');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleUnpublish = async (editionId) => {
    setProcessing(editionId + '_pub');
    try {
      await api.unpublishEdition(editionId);
      await fetchEditions();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  // Generate weekly edition with AI
  const handleGenerate = async () => {
    setGenerating(true); setGenStatus(null);
    try {
      const res = await api.generateWeeklyEdition();
      if (res.success) {
        setGenTaskId(res.task_id);
        setGenStatus({ status: 'processing', progress: 0 });
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
      setGenerating(false);
    }
  };

  const handleRegenerateImages = async (editionId) => {
    setProcessing(editionId + '_imgs');
    try {
      const res = await api.generateEditionImages(editionId);
      if (res.success) {
        setGenTaskId(res.task_id);
        setGenStatus({ status: 'processing', progress: 0, type: 'images' });
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handlePublishSocial = async (editionId) => {
    if (!confirm('¿Publicar portada + todos los artículos en X e Instagram?')) return;
    setProcessing(editionId + '_social');
    try {
      const res = await api.publishEditionToSocial(editionId);
      if (res.success) {
        setGenTaskId(res.task_id);
        setGenStatus({ status: 'processing', progress: 0, type: 'social' });
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  // Poll generation task
  useEffect(() => {
    if (!genTaskId) return;
    const interval = setInterval(async () => {
      try {
        const status = await api.getAITaskStatus(genTaskId);
        setGenStatus(status);
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(interval);
          setGenerating(false);
          if (status.status === 'completed') fetchEditions();
        }
      } catch { clearInterval(interval); setGenerating(false); }
    }, 4000);
    return () => clearInterval(interval);
  }, [genTaskId, fetchEditions]);

  const handleOpenArticleTweet = (edition, articleIndex) => {
    const article = (edition.sections || [])[articleIndex];
    if (!article) return;
    const url = `https://latechnologia.com/edition/${edition.edition_id}/article/${articleIndex}`;
    setTweetModal({
      editionId: edition.edition_id,
      articleIndex,
      title: article.title,
      tweetText: `${article.title}\n\n#LaTecnologia #EdiciónSemanal\n${url}`,
      loading: false
    });
  };

  const handlePublishArticleTweet = async () => {
    if (!tweetModal) return;
    setTweetModal(prev => ({ ...prev, loading: true }));
    try {
      const res = await api.publishEditionArticleTweet(tweetModal.editionId, tweetModal.articleIndex, tweetModal.tweetText);
      if (res.success) {
        alert('Tweet publicado');
        setTweetModal(null);
      } else {
        alert('Error: ' + (res.error || 'No se pudo publicar'));
        setTweetModal(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
      setTweetModal(prev => ({ ...prev, loading: false }));
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Ediciones Semanales
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {editions.length} ediciones - Crear y gestionar ediciones editoriales
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchEditions} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={handleGenerate} disabled={generating} className="bg-black text-white hover:bg-gray-800">
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {generating ? 'Generando...' : 'Generar Edición con IA'}
          </Button>
        </div>
      </div>

      {/* Generation progress */}
      {genStatus && (
        <div className={`p-4 rounded-lg border ${genStatus.status === 'completed' ? 'bg-green-50 border-green-200' : genStatus.status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
          {genStatus.status === 'processing' && (
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">Generando edición semanal...</p>
                <p className="text-xs text-blue-600 mt-1">Investigando noticias, escribiendo 7 ensayos y creando portada estilo The Economist</p>
                <div className="w-full bg-blue-200 rounded-full h-1.5 mt-2">
                  <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${genStatus.progress || 5}%` }} />
                </div>
              </div>
            </div>
          )}
          {genStatus.status === 'completed' && (
            <p className="text-sm font-medium text-green-800 flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Edición "{genStatus.result?.title}" creada con {genStatus.result?.articles_count} artículos
            </p>
          )}
          {genStatus.status === 'failed' && (
            <p className="text-sm text-red-700">Error: {genStatus.error}</p>
          )}
        </div>
      )}

      {editions.length === 0 ? (
        <div className="bg-white rounded-xl border p-8 text-center">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay ediciones</p>
          <Button onClick={() => setShowCreateModal(true)} className="mt-4">
            <Plus className="w-4 h-4 mr-2" />
            Crear Primera Edición
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {editions.map((edition) => (
            <EditionCard
              key={edition.edition_id}
              edition={edition}
              onEdit={() => setEditingEdition(edition)}
              onDelete={() => handleDelete(edition.edition_id)}
              onPublish={() => handlePublish(edition.edition_id)}
              onUnpublish={() => handleUnpublish(edition.edition_id)}
              onRegenerateImages={() => handleRegenerateImages(edition.edition_id)}
              onPublishSocial={() => handlePublishSocial(edition.edition_id)}
              processing={processing}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <EditionEditModal
          onClose={() => setShowCreateModal(false)}
          onSave={async (data) => {
            await api.createEdition(data);
            await fetchEditions();
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Individual Articles Editor */}
      {editingEdition && (
        <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto">
          <div className="min-h-screen py-6 px-4">
            <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-2xl">
              <div className="flex items-center justify-between p-5 border-b">
                <div>
                  <h2 className="text-lg font-bold">{editingEdition.title}</h2>
                  <p className="text-xs text-gray-500">{(editingEdition.sections || []).length} artículos · Semana {editingEdition.week_number}</p>
                </div>
                <Button variant="ghost" onClick={() => { setEditingEdition(null); fetchEditions(); }}><span className="text-xl">×</span></Button>
              </div>
              <div className="p-5 space-y-3">
                {(editingEdition.sections || []).map((section, idx) => {
                  const sImg = section.image ? (section.image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${section.image}` : section.image) : null;
                  const isExpanded = expandedArticle === idx;
                  return (
                    <div key={idx} className="border rounded-lg overflow-hidden">
                      {/* Header row */}
                      <div className="p-4 flex gap-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpandedArticle(isExpanded ? null : idx)}>
                        <div className="w-20 h-16 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                          {sImg ? <img src={sImg} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><Image className="w-5 h-5" /></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          {section.type === 'opinion' && <span className="text-[10px] font-bold uppercase tracking-wider text-red-500">Opinión</span>}
                          <h4 className="font-bold text-sm line-clamp-1">{section.title}</h4>
                          <p className="text-xs text-gray-500">Por {section.author} · {(section.content || '').replace(/<[^>]*>/g,'').length} chars</p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                      
                      {/* Expanded editor */}
                      {isExpanded && (
                        <div className="border-t p-4 bg-gray-50 space-y-3">
                          {/* Title */}
                          <div>
                            <label className="text-xs font-bold text-gray-500 mb-1 block">Título</label>
                            <Input value={section.title || ''} onChange={(e) => {
                              const u = [...editingEdition.sections]; u[idx].title = e.target.value;
                              setEditingEdition(p => ({...p, sections: u}));
                            }} />
                          </div>
                          {/* Author */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-bold text-gray-500 mb-1 block">Autor</label>
                              <Input value={section.author || ''} onChange={(e) => {
                                const u = [...editingEdition.sections]; u[idx].author = e.target.value;
                                setEditingEdition(p => ({...p, sections: u}));
                              }} />
                            </div>
                            <div>
                              <label className="text-xs font-bold text-gray-500 mb-1 block">Tipo</label>
                              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={section.type || 'news'}
                                onChange={(e) => { const u = [...editingEdition.sections]; u[idx].type = e.target.value; setEditingEdition(p => ({...p, sections: u})); }}>
                                <option value="news">Noticia</option>
                                <option value="opinion">Opinión</option>
                              </select>
                            </div>
                          </div>
                          {/* Content */}
                          <div>
                            <label className="text-xs font-bold text-gray-500 mb-1 block">Contenido</label>
                            <Textarea rows={8} value={(section.content || '').replace(/<p>/g,'\n').replace(/<\/p>/g,'').trim()} 
                              onChange={(e) => {
                                const u = [...editingEdition.sections]; 
                                u[idx].content = e.target.value.split('\n').filter(p=>p.trim()).map(p=>`<p>${p.trim()}</p>`).join('');
                                setEditingEdition(p => ({...p, sections: u}));
                              }} className="text-sm font-mono" />
                          </div>
                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button size="sm" className="bg-black text-white hover:bg-gray-800 h-8 text-xs" onClick={async () => {
                              const u = [...editingEdition.sections];
                              await api.updateEdition(editingEdition.edition_id, {sections: u});
                              alert('Guardado');
                            }}><Save className="w-3 h-3 mr-1" /> Guardar</Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={async () => {
                              const instrucciones = prompt('Instrucciones para la IA (ej: "hazlo más corto", "agrega datos"):');
                              if (!instrucciones) return;
                              try {
                                const r = await api.improveArticleText({title: section.title, content: section.content, instructions: instrucciones});
                                const u = [...editingEdition.sections];
                                u[idx].content = r.improved_content || u[idx].content;
                                u[idx].title = r.improved_title || u[idx].title;
                                setEditingEdition(p => ({...p, sections: u}));
                                alert('Texto mejorado con IA');
                              } catch(e) { alert(e.response?.data?.detail || e.message); }
                            }}><Sparkles className="w-3 h-3 mr-1" /> Editar con IA</Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={async () => {
                              try { const r = await api.regenerateEditionArticleImage(editingEdition.edition_id, idx);
                                if (r.success) { const u=[...editingEdition.sections]; u[idx].image=r.image; setEditingEdition(p=>({...p,sections:u})); alert('Imagen regenerada'); }
                              } catch(e) { alert(e.response?.data?.detail || e.message); }
                            }}><Image className="w-3 h-3 mr-1" /> Regen. img</Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleOpenArticleTweet(editingEdition, idx)}>
                              <Twitter className="w-3 h-3 mr-1" /> Tweet</Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={async () => {
                              if (!confirm('¿Publicar en Instagram?')) return;
                              try { const r = await api.publishEditionArticleInstagram(editingEdition.edition_id, idx);
                                alert(r.success ? 'Publicado en IG' : 'Error'); } catch(e) { alert(e.response?.data?.detail || e.message); }
                            }}><Send className="w-3 h-3 mr-1" /> IG</Button>
                            <Button size="sm" variant="ghost" className="h-8 text-xs text-red-500 ml-auto" onClick={async () => {
                              if (!confirm('¿Eliminar este artículo?')) return;
                              const u = editingEdition.sections.filter((_,i) => i !== idx);
                              await api.updateEdition(editingEdition.edition_id, {sections: u});
                              setEditingEdition(p => ({...p, sections: u}));
                            }}><Trash2 className="w-3 h-3 mr-1" /> Eliminar</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add new article */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <h4 className="text-sm font-bold mb-3">Agregar artículo</h4>
                  {addingArticle ? (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800">Investigando y generando artículo...</p>
                        <p className="text-xs text-blue-600 mt-1">Firecrawl investiga → IA escribe → Genera imagen</p>
                        <div className="w-full bg-blue-200 rounded-full h-1.5 mt-2">
                          <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${addingProgress}%` }} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={async () => {
                        const topic = prompt('Tema del artículo (la IA investigará con Firecrawl y escribirá):');
                        if (!topic) return;
                        setAddingArticle(true); setAddingProgress(5);
                        try {
                          const r = await api.addArticleToEditionAI(editingEdition.edition_id, topic);
                          if (r.success && r.task_id) {
                            // Poll progress
                            const poll = setInterval(async () => {
                              try {
                                const s = await api.getAITaskStatus(r.task_id);
                                setAddingProgress(s.progress || 10);
                                if (s.status === 'completed') {
                                  clearInterval(poll);
                                  setAddingArticle(false);
                                  // Reload edition
                                  const ed = await api.getEdition(editingEdition.edition_id);
                                  setEditingEdition(ed);
                                  alert('Artículo agregado: ' + (s.result?.title || ''));
                                } else if (s.status === 'failed') {
                                  clearInterval(poll);
                                  setAddingArticle(false);
                                  alert('Error: ' + (s.error || 'Falló'));
                                }
                              } catch { clearInterval(poll); setAddingArticle(false); }
                            }, 4000);
                          }
                        } catch(e) { setAddingArticle(false); alert(e.response?.data?.detail || e.message); }
                      }}><Sparkles className="w-4 h-4 mr-1" /> Con IA + Firecrawl</Button>
                      <Button size="sm" variant="outline" onClick={() => {
                        const title = prompt('Título del artículo:');
                        if (!title) return;
                        const u = [...editingEdition.sections, {title, content: '<p>Escribe el contenido aquí...</p>', author: 'Editorial', type: 'news', image: null}];
                        api.updateEdition(editingEdition.edition_id, {sections: u}).then(() => setEditingEdition(p => ({...p, sections: u})));
                      }}><PenTool className="w-4 h-4 mr-1" /> Manual</Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4 border-t flex justify-end">
                <Button variant="outline" onClick={() => { setEditingEdition(null); fetchEditions(); }}>Cerrar</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tweet Modal */}
      {tweetModal && (
        <Dialog open={true} onOpenChange={() => setTweetModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Twitter className="w-5 h-5" /> Publicar en X</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-gray-500 font-medium">{tweetModal.title}</p>
              <Textarea
                value={tweetModal.tweetText}
                onChange={(e) => setTweetModal(prev => ({ ...prev, tweetText: e.target.value }))}
                rows={5}
                disabled={tweetModal.loading}
              />
              <p className="text-xs text-gray-400">{tweetModal.tweetText?.length || 0}/280</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTweetModal(null)}>Cancelar</Button>
              <Button onClick={handlePublishArticleTweet} disabled={tweetModal.loading} className="bg-black text-white hover:bg-gray-800">
                {tweetModal.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Publicar Tweet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// ======================= EDITION CARD =======================
const EditionCard = ({ edition, onEdit, onDelete, onPublish, onUnpublish, onRegenerateImages, onPublishSocial, processing }) => {
  const coverImage = edition.cover_image ? (edition.cover_image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${edition.cover_image}` : edition.cover_image) : null;
  const articleCount = (edition.sections || []).length;
  const withImages = (edition.sections || []).filter(s => s.image).length;
  
  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <div className="relative aspect-[3/4] bg-gray-900">
        {coverImage ? (
          <img src={coverImage} alt="" className="w-full h-full object-cover opacity-80" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-12 h-12 text-gray-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute top-2 right-2">
          <Badge className={edition.status === 'published' ? 'bg-green-500' : 'bg-yellow-500'}>
            {edition.status === 'published' ? 'Publicada' : 'Borrador'}
          </Badge>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <p className="text-xs text-white/60 mb-1">Semana {edition.week_number}, {edition.year} · {articleCount} artículos</p>
          <h3 className="font-bold text-lg line-clamp-2">{edition.title}</h3>
        </div>
      </div>
      
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-1 flex-wrap">
          <Button size="sm" variant="outline" onClick={onEdit}><Edit2 className="w-3 h-3 mr-1" /> Editar</Button>
          <Button size="sm" variant="outline" onClick={onRegenerateImages} disabled={processing === edition.edition_id + '_imgs'}>
            <Image className="w-3 h-3 mr-1" /> Regen. imágenes {withImages < articleCount && `(${withImages}/${articleCount})`}
          </Button>
          <Button size="sm" variant="outline" onClick={onPublishSocial} disabled={processing === edition.edition_id + '_social'}>
            <Twitter className="w-3 h-3 mr-1" /> Redes
          </Button>
          <Button size="sm" variant="ghost" className="text-red-500" onClick={onDelete}><Trash2 className="w-3 h-3" /></Button>
        </div>
        <div>
          {edition.status === 'published' ? (
            <Button size="sm" variant="outline" className="w-full" onClick={onUnpublish} disabled={processing === edition.edition_id + '_pub'}>
              Despublicar
            </Button>
          ) : (
            <Button size="sm" className="w-full" onClick={onPublish} disabled={processing === edition.edition_id + '_pub'}>
              Publicar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ======================= EDITION EDIT MODAL =======================
const EditionEditModal = ({ edition, onClose, onSave, onTweetArticle }) => {
  const currentWeek = Math.ceil((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
  const currentYear = new Date().getFullYear();
  
  const [form, setForm] = useState({
    week_number: edition?.week_number || currentWeek,
    year: edition?.year || currentYear,
    title: edition?.title || '',
    summary: edition?.summary || '',
    cover_image: edition?.cover_image || '',
    cover_data: edition?.cover_data || {
      coverlines: [],
      mainHeadline: '',
      personName: '',
      personDescription: '',
      quote: '',
    },
    sections: edition?.sections || [],
    featured_dossiers: edition?.featured_dossiers || [],
  });
  
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [suggestingField, setSuggestingField] = useState(null);
  const [aiInstruction, setAiInstruction] = useState('');
  const [showAiInput, setShowAiInput] = useState(null);

  const handleSubmit = async () => {
    if (!form.title) {
      alert('El título es requerido');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAiSuggest = async (field) => {
    setSuggestingField(field);
    try {
      const context = {
        title: form.title,
        personName: form.cover_data?.personName,
        week: form.week_number,
        year: form.year,
      };
      
      const res = await api.suggestEditionContent({
        field,
        instruction: aiInstruction,
        context,
      });
      
      if (res.success && res.suggestion) {
        if (field === 'coverlines') {
          const lines = res.suggestion.split('\n').filter(l => l.trim());
          setForm(prev => ({
            ...prev,
            cover_data: { ...prev.cover_data, coverlines: lines }
          }));
        } else if (['mainHeadline', 'personDescription', 'quote'].includes(field)) {
          setForm(prev => ({
            ...prev,
            cover_data: { ...prev.cover_data, [field]: res.suggestion }
          }));
        } else if (field === 'summary') {
          setForm(prev => ({ ...prev, summary: res.suggestion }));
        }
        setShowAiInput(null);
        setAiInstruction('');
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSuggestingField(null);
    }
  };

  const updateCoverData = (key, value) => {
    setForm(prev => ({
      ...prev,
      cover_data: { ...prev.cover_data, [key]: value }
    }));
  };

  const addSection = () => {
    setForm(prev => ({
      ...prev,
      sections: [...prev.sections, {
        section_type: 'cover_story',
        title: '',
        content: '',
        author: '',
      }]
    }));
  };

  const updateSection = (index, key, value) => {
    const sections = [...form.sections];
    sections[index] = { ...sections[index], [key]: value };
    setForm(prev => ({ ...prev, sections }));
  };

  const removeSection = (index) => {
    setForm(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const AiSuggestButton = ({ field, label }) => (
    <div className="relative">
      {showAiInput === field ? (
        <div className="flex gap-2 items-center">
          <Input
            value={aiInstruction}
            onChange={(e) => setAiInstruction(e.target.value)}
            placeholder="Instrucción para la IA (opcional)"
            className="text-sm flex-1"
            onKeyPress={(e) => e.key === 'Enter' && handleAiSuggest(field)}
          />
          <Button
            size="sm"
            onClick={() => handleAiSuggest(field)}
            disabled={suggestingField === field}
          >
            {suggestingField === field ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowAiInput(null)}>
            ×
          </Button>
        </div>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          className="text-purple-600 hover:text-purple-700 h-7 px-2"
          onClick={() => setShowAiInput(field)}
        >
          <Sparkles className="w-3 h-3 mr-1" />
          Sugerir
        </Button>
      )}
    </div>
  );

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            {edition ? 'Editar Edición' : 'Nueva Edición'}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b pb-2">
          {[
            { id: 'general', label: 'General', icon: FileText },
            { id: 'cover', label: 'Portada', icon: Image },
            { id: 'sections', label: 'Secciones', icon: Newspaper },
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="w-4 h-4 mr-1" />
              {tab.label}
            </Button>
          ))}
        </div>

        <div className="space-y-6 py-4">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Semana</label>
                  <Input
                    type="number"
                    value={form.week_number}
                    onChange={(e) => setForm({ ...form, week_number: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Año</label>
                  <Input
                    type="number"
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Título de la Edición</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ej: Latinoamérica en Perspectiva"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Resumen</label>
                  <AiSuggestButton field="summary" />
                </div>
                <Textarea
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  placeholder="Breve descripción de esta edición..."
                  rows={3}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">URL de Imagen de Portada</label>
                <Input
                  value={form.cover_image}
                  onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                  placeholder="https://..."
                />
                {form.cover_image && (
                  <img src={form.cover_image} alt="" className="mt-2 h-32 object-cover rounded" />
                )}
              </div>
            </div>
          )}

          {/* Cover Tab */}
          {activeTab === 'cover' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Configura los elementos de la portada editorial estilo Forbes
              </p>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Nombre de la Persona
                  </label>
                </div>
                <Input
                  value={form.cover_data?.personName || ''}
                  onChange={(e) => updateCoverData('personName', e.target.value)}
                  placeholder="Ej: Diego Urquijo"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Descripción de la Persona</label>
                  <AiSuggestButton field="personDescription" />
                </div>
                <Input
                  value={form.cover_data?.personDescription || ''}
                  onChange={(e) => updateCoverData('personDescription', e.target.value)}
                  placeholder="Ej: Arquitecto de sistemas empresariales"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Quote className="w-4 h-4" />
                    Cita
                  </label>
                  <AiSuggestButton field="quote" />
                </div>
                <Textarea
                  value={form.cover_data?.quote || ''}
                  onChange={(e) => updateCoverData('quote', e.target.value)}
                  placeholder='"Las ideas inspiran. Los sistemas gobiernan."'
                  rows={2}
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Titular Principal (Headline)</label>
                  <AiSuggestButton field="mainHeadline" />
                </div>
                <Input
                  value={form.cover_data?.mainHeadline || ''}
                  onChange={(e) => updateCoverData('mainHeadline', e.target.value)}
                  placeholder="EL PODER DE DISEÑAR SISTEMAS"
                  className="uppercase"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Coverlines (frases de portada)</label>
                  <AiSuggestButton field="coverlines" />
                </div>
                <Textarea
                  value={(form.cover_data?.coverlines || []).join('\n')}
                  onChange={(e) => updateCoverData('coverlines', e.target.value.split('\n').filter(l => l.trim()))}
                  placeholder="Una línea por frase..."
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">Una frase por línea</p>
              </div>
            </div>
          )}

          {/* Sections Tab */}
          {activeTab === 'sections' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Secciones de contenido de la edición
                </p>
                <Button size="sm" onClick={addSection}>
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar Sección
                </Button>
              </div>
              
              {form.sections.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Newspaper className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No hay secciones</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {form.sections.map((section, index) => (
                    <SectionEditor
                      key={index}
                      section={section}
                      index={index}
                      onUpdate={(key, value) => updateSection(index, key, value)}
                      onRemove={() => removeSection(index)}
                      onTweetArticle={onTweetArticle ? () => onTweetArticle(index) : null}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ======================= SECTION EDITOR =======================
const SectionEditor = ({ section, index, onUpdate, onRemove, onTweetArticle }) => {
  const [expanded, setExpanded] = useState(true);
  const [suggestingField, setSuggestingField] = useState(null);
  
  const sectionTypes = [
    { value: 'cover_story', label: 'Historia de Portada' },
    { value: 'entrevista_exclusiva', label: 'Entrevista Exclusiva' },
    { value: 'análisis', label: 'Análisis' },
    { value: 'impacto_latam', label: 'Impacto LATAM' },
    { value: 'opinion', label: 'Opinión' },
    { value: 'tendencias', label: 'Tendencias' },
  ];

  const handleAiSuggest = async (field) => {
    setSuggestingField(field);
    try {
      const res = await api.suggestEditionContent({
        field: field === 'title' ? 'section_title' : 'section_content',
        instruction: '',
        context: {
          section_type: section.section_type,
          current_title: section.title,
        },
      });
      
      if (res.success && res.suggestion) {
        onUpdate(field, res.suggestion);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSuggestingField(null);
    }
  };
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <div 
        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <span className="font-medium text-sm">
            {section.title || `Sección ${index + 1}`}
          </span>
          <Badge variant="outline" className="text-xs">
            {sectionTypes.find(t => t.value === section.section_type)?.label || section.section_type}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          {onTweetArticle && (
            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onTweetArticle(); }} title="Publicar en X">
              <Twitter className="w-3 h-3" />
            </Button>
          )}
          <Button 
            size="sm" 
            variant="ghost" 
            className="text-red-500"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
          >
          <Trash2 className="w-4 h-4" />
        </Button>
        </div>
      </div>
      
      {expanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Tipo de Sección</label>
              <select
                value={section.section_type}
                onChange={(e) => onUpdate('section_type', e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                {sectionTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Autor</label>
              <Input
                value={section.author || ''}
                onChange={(e) => onUpdate('author', e.target.value)}
                placeholder="Nombre del autor"
              />
            </div>
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium">Título</label>
              <Button
                size="sm"
                variant="ghost"
                className="text-purple-600 h-6 px-2"
                onClick={() => handleAiSuggest('title')}
                disabled={suggestingField === 'title'}
              >
                {suggestingField === 'title' ? (
                  <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <Sparkles className="w-3 h-3 mr-1" />
                )}
                Sugerir
              </Button>
            </div>
            <Input
              value={section.title || ''}
              onChange={(e) => onUpdate('title', e.target.value)}
              placeholder="Título de la sección"
            />
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium">Contenido</label>
              <Button
                size="sm"
                variant="ghost"
                className="text-purple-600 h-6 px-2"
                onClick={() => handleAiSuggest('content')}
                disabled={suggestingField === 'content'}
              >
                {suggestingField === 'content' ? (
                  <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <Sparkles className="w-3 h-3 mr-1" />
                )}
                Sugerir
              </Button>
            </div>
            <Textarea
              value={section.content || ''}
              onChange={(e) => onUpdate('content', e.target.value)}
              placeholder="Contenido de la sección..."
              rows={5}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EditionsManager;
